/**
 * Copy for transport-level failures: responses written by a proxy or CDN
 * rather than by the API.
 *
 * Only the statuses our own stack actually emits are listed. nginx answers
 * 502/503/504; Cloudflare answers 524 when it gives up waiting for the origin.
 * A gateway body is an HTML page, so it must never be shown to an operator.
 */

export const TRANSPORT_ERROR_STATUSES = [502, 503, 504, 524] as const;

export type TransportErrorStatus = (typeof TRANSPORT_ERROR_STATUSES)[number];

const TRANSPORT_ERROR_COPY: Record<TransportErrorStatus, string> = {
  502: 'The server could not be reached (bad gateway). This is usually temporary.',
  503: 'The server is temporarily unavailable. This is usually temporary.',
  504: 'The server took too long to respond and the connection timed out.',
  524: 'The connection timed out before the server finished responding.',
};

export const isTransportErrorStatus = (
  status: number | undefined,
): status is TransportErrorStatus =>
  (TRANSPORT_ERROR_STATUSES as readonly number[]).includes(status as number);

/** A gateway timed out rather than refusing: the request may still be running. */
export const isTimeoutStatus = (status: number | undefined): boolean =>
  status === 504 || status === 524;

/**
 * 502/503 mean the request never reached a working server, so repeating it is
 * worth offering. A timeout is not retryable here: the work that overran the
 * gateway will overrun it again on an identical request.
 */
export const isRetryableTransportStatus = (status: number | undefined): boolean =>
  status === 502 || status === 503;

/** Human sentence for a gateway failure; null for any status the API owns. */
export const transportErrorMessage = (status: number | undefined): string | null =>
  isTransportErrorStatus(status) ? TRANSPORT_ERROR_COPY[status] : null;

/** True when a response body is markup (a gateway error page), not text to show. */
export const isMarkupBody = (body: string): boolean =>
  /^\s*</.test(body) || /<\s*(!doctype|html|body|head)\b/i.test(body);
